import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import TimeSlotPicker from "./TimeSlotPicker";
import {
  parseYMDLocal,
  parseDateTimeLocal,
  formatYMD,
  dayOverlapsRanges,
  dayIsHostUnavailable,
  startOfDay,
} from "../lib/dateLocal";
import { API } from "../auth/AuthContext";

/**
 * Date and time as TWO separate fields side-by-side. value is "YYYY-MM-DDTHH:MM".
 *
 * Why a dedicated component instead of using DateTimePicker?
 *   - The user explicitly wanted "pickup date" and "pickup time" as separate
 *     visible fields with clear labels (different from the combined picker).
 *   - This gives each field its own label, helper text, and clearer affordance.
 *
 * Props:
 *   value, onChange — same contract as DateTimePicker
 *   label — singular, becomes the wrapper aria-label
 *   dateLabel / timeLabel — defaults: "Date" / "Time"
 *   minDate — same as DateTimePicker
 *   bookedRanges — same as DateTimePicker
 *   calendarMode / calendarDays — host blocked/rentable days (KAN-126)
 *   testidPrefix — used for both fields ("xxx-date", "xxx-time")
 */
export default function SplitDateTime({
  value,
  onChange,
  dateLabel = "Date",
  timeLabel = "Time",
  minDate,
  bookedRanges,
  calendarMode = "block_unavailable",
  calendarDays,
  testidPrefix,
  helperText,
  disabled,
  defaultTime,
  priceVehicleId,
}) {
  const datePart = value ? value.split("T")[0] : "";
  const fallbackTime = defaultTime || "10:00";
  const timePart = value ? (value.split("T")[1] || fallbackTime).slice(0, 5) : fallbackTime;
  const dateObj = parseYMDLocal(datePart);

  const [open, setOpen] = useState(false);

  const setDate = (d) => {
    if (!d) { onChange?.(""); return; }
    onChange?.(`${formatYMD(d)}T${timePart}`);
    setOpen(false);
  };
  const setTime = (t) => {
    if (!datePart) {
      const today = new Date();
      onChange?.(`${formatYMD(today)}T${t}`);
    } else {
      onChange?.(`${datePart}T${t}`);
    }
  };

  const minDateTime = parseDateTimeLocal(minDate);
  const minD = minDateTime || parseYMDLocal(minDate);
  const minDatePart = minDate && minDate.includes("T") ? minDate.split("T")[0] : (minDate || "");
  const minTimePart = minDate && minDate.includes("T") ? (minDate.split("T")[1] || "00:00").slice(0, 5) : "00:00";
  const sameDayAsMin = !!datePart && !!minDatePart && datePart === minDatePart;

  const isDisabled = (d) => {
    // Always disallow dates earlier than today.
    if (startOfDay(d).getTime() < startOfDay(new Date()).getTime()) return true;
    if (minD && startOfDay(d).getTime() < startOfDay(minD).getTime()) return true;
    if (dayOverlapsRanges(d, bookedRanges)) return true;
    if (dayIsHostUnavailable(d, calendarMode, calendarDays)) return true;
    return false;
  };

  const slots = [];
  for (let h = 0; h < 24; h++) for (const m of [0, 30]) {
    slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
  const availableSlots = slots.filter((s) => !(sameDayAsMin && s <= minTimePart));
  const activeTime = availableSlots.includes(timePart)
    ? timePart
    : (availableSlots[0] || timePart);

  useEffect(() => {
    if (!datePart || !availableSlots.length) return;
    if (timePart !== activeTime) {
      onChange?.(`${datePart}T${activeTime}`);
    }
  }, [datePart, activeTime, timePart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-correct: if value falls before minDate, snap to minDate.
  useEffect(() => {
    if (!value || !minDateTime) return;
    const cur = parseDateTimeLocal(value);
    if (cur && cur.getTime() < minDateTime.getTime()) {
      onChange?.(`${minDatePart}T${minTimePart}`);
    }
  }, [minDate, value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pricing tier overlay (cheap/avg/pricey dots in calendar grid)
  const [priceMap, setPriceMap] = useState({});
  const [listedDailyRate, setListedDailyRate] = useState(null);
  useEffect(() => {
    if (!priceVehicleId) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/vehicles/${priceVehicleId}/price-calendar?days=60`);
        if (!alive) return;
        const m = {};
        (r.data?.days || []).forEach((d) => { m[d.date] = d; });
        setPriceMap(m);
        setListedDailyRate(r.data?.base_rate ?? null);
      } catch (e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [priceVehicleId]);
  const { lowDays, midDays, highDays, hasPricing } = useMemo(() => {
    const low = [], mid = [], high = [];
    Object.values(priceMap).forEach((d) => {
      const dt = parseYMDLocal(d.date);
      if (!dt) return;
      if (d.tier === "low") low.push(dt);
      else if (d.tier === "high") high.push(dt);
      else mid.push(dt);
    });
    return { lowDays: low, midDays: mid, highDays: high, hasPricing: low.length + mid.length + high.length > 0 };
  }, [priceMap]);
  const priceFor = dateObj ? priceMap[formatYMD(dateObj)] : null;

  return (
    <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3 min-w-0">
      <div className="min-w-0">
        <label className="text-xs text-[var(--zc-text-3)] block mb-1.5 flex items-center gap-1.5">
          <CalendarIcon size={12} /> {dateLabel}
        </label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className="zc-input h-11 px-3 text-sm text-left flex items-center justify-between w-full min-w-0 gap-2"
              data-testid={`${testidPrefix}-date`}
            >
              <span className={"whitespace-nowrap " + (datePart ? "" : "text-[var(--zc-text-3)]")}>
                {dateObj ? format(dateObj, "dd/MM/yyyy") : "Select date"}
              </span>
              <CalendarIcon size={14} className="text-[var(--zc-text-3)] shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white border border-[var(--zc-border)] z-[100]" align="start" sideOffset={6}>
            <Calendar
              mode="single"
              selected={dateObj}
              onSelect={setDate}
              disabled={isDisabled}
              initialFocus
              modifiers={{ priceLow: lowDays, priceMid: midDays, priceHigh: highDays }}
              modifiersClassNames={{
                priceLow: "zc-day-price-low",
                priceMid: "zc-day-price-mid",
                priceHigh: "zc-day-price-high",
              }}
            />
            {hasPricing && (
              <div className="px-3 py-2 border-t border-[var(--zc-border)] flex items-center gap-3 zc-meta">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--zc-success)" }} /> Cheap</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--zc-secondary)" }} /> Average</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--zc-danger,#dc2626)" }} /> Pricey</span>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
      <div className="min-w-0">
        <label className="text-xs text-[var(--zc-text-3)] block mb-1.5 flex items-center gap-1.5">
          <CalendarIcon size={12} /> {timeLabel}
        </label>
        <TimeSlotPicker
          value={activeTime}
          onChange={setTime}
          slots={availableSlots}
          disabled={disabled}
          markAfterHours
          testid={`${testidPrefix}-time`}
        />
      </div>
      {helperText && (
        <div className="min-[420px]:col-span-2 zc-hint -mt-1 break-words">
          {helperText}
        </div>
      )}
      {priceFor && (
        <div className="min-[420px]:col-span-2 text-caption flex flex-wrap items-center gap-1.5 -mt-1" data-testid={`${testidPrefix}-price-hint`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: priceFor.tier === "low" ? "var(--zc-success)" : priceFor.tier === "high" ? "var(--zc-danger,#dc2626)" : "var(--zc-secondary)" }} />
          <span className="text-[var(--zc-text-3)]">
            ${priceFor.price} for that day
            {listedDailyRate != null && priceFor.price !== listedDailyRate && (
              <span className="text-[var(--zc-text-3)]"> · listed ${listedDailyRate}/day</span>
            )}
          </span>
          {priceFor.tier === "low" && <span className="text-[var(--zc-success)] font-semibold">· great deal</span>}
          {priceFor.tier === "high" && <span style={{ color: "var(--zc-danger,#dc2626)" }} className="font-semibold">· peak</span>}
        </div>
      )}
    </div>
  );
}
